import {messages} from '@smartmixin/i18n';
import {StatusCodes} from 'http-status-codes';
import React from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {AuthService, UserService, getApiUrl} from '../api';
import {UserDetail} from '../api/types';
import {
  clearStaffElevation,
  getDebugEmail,
  selectStaffElevation,
  setStaffElevation,
} from '../store';
import {CommonRootState} from '../store/types';
import {Dates} from '../utils';
import {subscribeToNothing, subscribeToSeconds} from './clockSubscription';
import useApiClient, {ApiError} from './useApiClient';

/** The 403 `detail.code` for a staff user with no TOTP / recovery codes. */
const MFA_NOT_ENROLLED = 'mfa_not_enrolled';

/**
 * Where an admin without a code-producing factor adds one: the back office.
 * Null when the API root is not an absolute URL -- the webapp's dev server
 * runs with an empty root (relative `/api`, proxied by Vite), where `/bo/...`
 * would resolve against the dev server and 404.
 */
const mfaSetupUrl = () => {
  const root = getApiUrl();
  return /^https?:\/\//.test(root) ? `${root}/bo/account/setup/` : null;
};

export type StaffElevationError = {
  /** Ready to display. */
  message: string;
  /** Set only when the admin has no second factor enrolled yet. */
  setupUrl: string | null;
};

type Fetch = ReturnType<typeof useApiClient>['fetch'];
type EditUserDetails = (data: UserDetail) => void;

/**
 * Whole minutes left, rounded UP so the last minute reads "1 min" and 0 means
 * expired. Minutes, not seconds, so the clock snapshot below changes once a
 * minute and the screen re-renders at that rate rather than every second.
 */
const minutesUntil = (expiresAt: number) =>
  Math.max(0, Math.ceil((expiresAt - Date.now() / 1000) / 60));

/**
 * The Debug screens' view of the staff second factor.
 *
 * `fetch` and `editUserDetails` are the screen's own `useApiClient().fetch`
 * and `useUserEdit()`, passed in rather than created here so each app's tests
 * drive this hook through the same mocks as the rest of the screen.
 *
 * Once elevated, a Login As that the backend refused for want of a code is
 * completed: the target's details are fetched once, exactly as the Login As
 * button does. Nothing is retried automatically beyond that.
 *
 * Nothing here decides WHEN to ask for a code: `required` is raised by
 * useApiClient on the backend's `staff_elevation_required` 403 and by nothing
 * else, so the prompt cannot appear before the backend enforces the factor.
 */
const useStaffElevation = (fetch: Fetch, editUserDetails: EditUserDetails) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const store = useStore<CommonRootState>();
  // A ref, not the `submitting` state: two submits in the same tick (the
  // keyboard's submit key bypasses the disabled button) both see the state
  // before React re-renders. A TOTP sent twice is a replay 400 that also
  // spends the backend's rate limit.
  const inFlightRef = React.useRef(false);
  const {token, expiresAt, required} = useSelector(selectStaffElevation);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<StaffElevationError | null>(null);

  const holdsToken = token !== null && expiresAt !== null;
  const minutesLeft = React.useSyncExternalStore(
    holdsToken ? subscribeToSeconds : subscribeToNothing,
    () =>
      token !== null && expiresAt !== null ? minutesUntil(expiresAt) : null,
  );

  React.useEffect(() => {
    if (minutesLeft === 0) {
      dispatch(clearStaffElevation());
    }
  }, [minutesLeft, dispatch]);

  const describe = React.useCallback(
    (reason: unknown): StaffElevationError => {
      if (reason instanceof ApiError) {
        if (reason.statusCode === StatusCodes.BAD_REQUEST) {
          // Wrong, reused or rate-limited code: the backend's own message
          // for the `code` field, which says which of those it was.
          return {
            message:
              reason.fields.code?.message ||
              intl.formatMessage(messages.auth.staffVerificationInvalidCode),
            setupUrl: null,
          };
        }
        if (
          reason.statusCode === StatusCodes.FORBIDDEN &&
          reason.details?.code === MFA_NOT_ENROLLED
        ) {
          return {
            message: intl.formatMessage(
              messages.auth.staffVerificationNotEnrolled,
            ),
            setupUrl: mfaSetupUrl(),
          };
        }
        if (reason.details?.message) {
          return {message: reason.details.message, setupUrl: null};
        }
      }
      return {
        message: intl.formatMessage(messages.network.unknownError),
        setupUrl: null,
      };
    },
    [intl],
  );

  const submit = React.useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed || inFlightRef.current) {
        return false;
      }
      inFlightRef.current = true;
      setSubmitting(true);
      setError(null);
      try {
        const res = await fetch(AuthService.elevate(trimmed), {
          skipLoginAs: true,
          // Every failure is shown inline next to the code field.
          silent: true,
        });
        dispatch(
          setStaffElevation({
            token: res.elevation_token,
            expiresAt: res.expires_at,
          }),
        );
        if (getDebugEmail(store.getState())) {
          // Not awaited: the elevation succeeded whatever this returns.
          fetch(UserService.getUserDetail())
            .then((data) => {
              editUserDetails(data);
            })
            .catch(() => {});
        }
        return true;
      } catch (reason) {
        setError(describe(reason));
        return false;
      } finally {
        inFlightRef.current = false;
        setSubmitting(false);
      }
    },
    [fetch, dispatch, describe, store, editUserDetails],
  );

  const elevated = minutesLeft !== null && minutesLeft > 0;

  return {
    /** Show the code prompt. */
    required: required && !elevated,
    elevated,
    /** e.g. "Verified. Expires in 11 hr 59 min." -- null unless elevated. */
    expiresInLabel: elevated
      ? intl.formatMessage(messages.auth.staffVerificationExpiresIn, {
          duration: Dates.formatDuration(minutesLeft * 60, intl),
        })
      : null,
    submitting,
    error,
    /** Resolves true once elevated; false (with `error` set) otherwise. */
    submit,
  };
};

export default useStaffElevation;
